/**
 * The handover from the static transit shell in index.html to the React one.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: `index.html` carries a hand-written twin of
 * `AuthTransitShell`'s painted output -- the same artwork inlined as SVG, everything else
 * spelled as inline styles rather than Tailwind classes, no network request of any kind --
 * so the mark and the line are on screen from the FIRST PAINT of the document rather than
 * after the bundle has downloaded, parsed and mounted. A synchronous inline script
 * immediately after the markup removes it on every path except the two transit routes below.
 *
 * It is a twin, not a copy: the two are the same PAINTED RESULT written in two different
 * languages, and `staticTransitShell.test.ts` is what holds them to it. That test resolves
 * each Tailwind class this side uses to the value the built stylesheet gives it and compares
 * against the literal over there, and it compares the mark's artwork attribute by attribute.
 * It cannot compare bytes and never could -- one side has classes, the other declarations.
 *
 * WHY THE SHELL IS A SIBLING OF #app AND NOT A CHILD. The tidy version of this puts the
 * markup inside the root element and lets `createRoot()` clear its container on the
 * first commit, so React "replaces" it with no removal code. Measured against the built
 * logto-ui bundle, that version is wrong and fails silently: the container clear happens
 * as soon as the bundle runs, which is long before the app has anything to paint (over
 * there it waits on a settings round trip; here it waits on the lazy route chunk and on
 * the identity check). The observed result was the shell at t=25ms and an EMPTY root at
 * t=50ms -- the blank simply moved later. Outside the root element it survives the clear,
 * and this hook takes it down.
 *
 * `useLayoutEffect`, not `useEffect`. A passive effect runs AFTER paint, so on the
 * branches where the screen does NOT render the transit shell -- `/auth/link`'s gate
 * screens, `/callback`'s error card -- there would be one painted frame with a stale
 * "Signing you in" sitting over the real answer. A layout effect takes it down in the
 * same frame the screen's own first paint lands, so there is never a frame with neither
 * and never a frame with a visible difference.
 *
 * CALL IT FROM THE SCREEN, NOT FROM `AuthTransitShell`. `/auth/link` renders a gate
 * rather than the shell whenever it is not auto-proceeding, and `/callback` renders an
 * error card when the exchange fails. If only the shell removed the static copy, those
 * branches would keep it pinned over the screen forever. The screen component's first
 * commit is the event we actually mean: "React now owns this document".
 *
 * If the bundle never boots, the hook never runs and the static shell simply stays up
 * saying "Signing you in". That is the intended failure mode: a branded, honest waiting
 * state beats a bare cream field, and it is the same surface the visitor was already
 * looking at on the far side of the redirect.
 */
import { useLayoutEffect } from 'react';

import { AUTH_LINK_PATH } from './authLinkParams';

/** The id authored on the static shell in index.html. Cross-file contract, pinned by test. */
export const STATIC_TRANSIT_SHELL_ID = 'mb-transit-shell';

/** The webapp's half of the return hop. Not a constant anywhere else in the app today. */
export const CALLBACK_PATH = '/callback';

/**
 * The only two paths that may paint the shell before JS runs, and the source of truth for
 * the inline gate in index.html. Both are documents the visitor lands on mid-journey with
 * one decision already made; every other route would be flashing a sign-in message at
 * someone who did not ask to sign in.
 */
export const STATIC_TRANSIT_SHELL_PATHS = [CALLBACK_PATH, AUTH_LINK_PATH] as const;

/**
 * Takes the static shell down. Idempotent, and a no-op on every route that never
 * painted one.
 *
 * TWO callers, and the second is not a magic-link screen at all. The hook below is the happy
 * path: the screen commits and owns the document. The other is the app-wide ErrorBoundary
 * (`src/components/ErrorBoundary.tsx`), which is the case where neither screen ever commits.
 * Its fallback renders INSIDE `#app`, in flow, with no stacking context of its own, so on
 * `/callback` or `/auth/link` a throw above the router left "Signing you in" pinned over the
 * Reload CTA at `z-index: 1` for the life of the document -- the removal only runs on a path
 * that is no longer being taken. Removing the node is the fix rather than out-stacking it: a
 * `z-index` on the fallback would still leave a full-viewport cream sheet in the document,
 * over whatever the app paints next.
 */
export const removeStaticTransitShell = (): void => {
  document.querySelector(`#${STATIC_TRANSIT_SHELL_ID}`)?.remove();
};

export const useStaticTransitShellHandover = (): void => {
  useLayoutEffect(() => {
    removeStaticTransitShell();
  }, []);
};
